import { useEffect, useState } from "react";
import { Users, Lock, Loader2, Trash2, Plus, KeyRound, Copy, Check, Crown } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);
const inp = "bg-navy-950 border border-gold-500/25 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-gold-500/60 w-full";
const ROLE_HINTS = { admin: "Administrator — full access incl. Business Hub, Team, Leads", staff: "Staff — dashboard + Business Hub, no team admin", client: "Client — portal access only" };
const LEVEL_HINTS = { none: "No task board", member: "Own board only", manager: "All boards, no board admin", administrator: "Manage boards + all tasks" };

function genPassword() {
  const abc = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  const pick = (n) => Array.from({ length: n }, () => abc[Math.floor(Math.random() * abc.length)]).join("");
  return `${pick(4)}-${pick(4)}`;
}

function InviteForm({ onDone }) {
  const [f, setF] = useState({ name: "", email: "", password: "", role: "staff", board_level: "member", board_name: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/team", { ...f, password: f.password || null, board_name: f.board_name || null });
      onDone(data, f.password || data.generated_password);
      setF({ name: "", email: "", password: "", role: "staff", board_level: "member", board_name: "" });
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not add the team member.");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="border border-gold-500/25 bg-navy-900/50 p-5 space-y-4" data-testid="team-invite-form">
      <Mono className="text-[0.6rem] text-gold-400">Add a team sign-in</Mono>
      <div className="grid sm:grid-cols-2 gap-3">
        <input value={f.name} onChange={set("name")} placeholder="Full name" required className={inp} data-testid="team-name-input" />
        <input value={f.email} onChange={set("email")} placeholder="email@cbkm.au" type="email" required className={inp} data-testid="team-email-input" />
        <div className="flex gap-2">
          <input value={f.password} onChange={set("password")} placeholder="Password (blank = generate)" className={inp} data-testid="team-password-input" />
          <button type="button" onClick={() => setF((s) => ({ ...s, password: genPassword() }))} data-testid="team-genpass-btn"
            className="shrink-0 border border-gold-500/30 text-gold-300 px-3 hover:bg-gold-500/10" title="Generate password"><KeyRound className="h-4 w-4" /></button>
        </div>
        <select value={f.role} onChange={set("role")} className={inp} data-testid="team-role-select">
          {Object.entries(ROLE_HINTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={f.board_level} onChange={set("board_level")} className={inp} data-testid="team-level-select">
          {Object.entries(LEVEL_HINTS).map(([k, v]) => <option key={k} value={k}>Task board: {v}</option>)}
        </select>
        {f.board_level !== "none" && (
          <input value={f.board_name} onChange={set("board_name")} placeholder="Board name (optional)" className={inp} data-testid="team-boardname-input" />
        )}
      </div>
      {err && <p className="text-amber-300 text-xs" data-testid="team-invite-error">{err}</p>}
      <button type="submit" disabled={busy} data-testid="team-invite-submit"
        className="inline-flex items-center gap-2 bg-gold-500 text-navy-950 px-5 py-2.5 text-sm font-medium hover:bg-gold-400 disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add to team
      </button>
    </form>
  );
}

function CredsBanner({ creds, onClose }) {
  const [copied, setCopied] = useState(false);
  const text = `MDAu Office sign-in\nEmail: ${creds.email}\nPassword: ${creds.password}\nURL: ${window.location.origin}/login`;
  const copy = () => { navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }); };
  return (
    <div className="border border-emerald-400/40 bg-emerald-400/5 p-4 flex items-start justify-between gap-4" data-testid="team-creds-banner">
      <div>
        <Mono className="text-[0.55rem] text-emerald-300">Sign-in ready — share these credentials once</Mono>
        <p className="text-white text-sm mt-2 font-mono">{creds.email} · {creds.password}</p>
        <p className="text-white/45 text-xs font-light mt-1">Passwords are never shown again. Use “Reset password” later if needed.</p>
      </div>
      <div className="flex gap-2 shrink-0">
        <button onClick={copy} data-testid="team-creds-copy" className="border border-emerald-400/40 text-emerald-300 px-3 py-2 text-xs hover:bg-emerald-400/10 inline-flex items-center gap-1.5">
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? "Copied" : "Copy"}
        </button>
        <button onClick={onClose} className="text-white/40 hover:text-white text-xs">Dismiss</button>
      </div>
    </div>
  );
}

function MemberRow({ m, self, onPatch, onRemove }) {
  const [busy, setBusy] = useState(false);
  const patch = async (body) => { setBusy(true); try { await onPatch(m.id, body); } finally { setBusy(false); } };
  const resetPw = async () => {
    const pw = genPassword();
    if (!window.confirm(`Reset ${m.name}'s password to ${pw}?`)) return;
    await patch({ password: pw });
    window.alert(`New password for ${m.email}: ${pw}\n\nCopy it now — it will not be shown again.`);
  };
  return (
    <tr className="border-b border-white/5" data-testid={`team-row-${m.id}`}>
      <td className="py-3 pr-4">
        <span className="text-white text-sm flex items-center gap-1.5">{m.name}{m.is_owner && <Crown className="h-3.5 w-3.5 text-gold-400" title="Org owner" />}</span>
        <span className="text-white/40 text-xs font-mono block">{m.email}</span>
      </td>
      <td className="py-3 pr-4">
        <select value={m.role} disabled={busy || self} onChange={(e) => patch({ role: e.target.value })} data-testid={`team-role-${m.id}`}
          className="bg-navy-950 border border-gold-500/25 rounded-sm px-2 py-1.5 text-xs disabled:opacity-50">
          <option value="admin">Administrator</option><option value="staff">Staff</option><option value="client">Client</option>
        </select>
      </td>
      <td className="py-3 pr-4">
        <select value={m.board_level} disabled={busy} onChange={(e) => patch({ board_level: e.target.value })} data-testid={`team-level-${m.id}`}
          className="bg-navy-950 border border-gold-500/25 rounded-sm px-2 py-1.5 text-xs disabled:opacity-50">
          {Object.keys(LEVEL_HINTS).map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        {m.board_name && <span className="text-white/35 text-[0.65rem] block mt-1">board: {m.board_name}</span>}
      </td>
      <td className="py-3 text-right whitespace-nowrap">
        <button onClick={resetPw} disabled={busy} data-testid={`team-resetpw-${m.id}`} className="text-gold-400/70 hover:text-gold-300 text-xs mr-4 disabled:opacity-50">Reset password</button>
        {!self && !m.is_owner && (
          <button onClick={() => window.confirm(`Remove ${m.name} from the team? Their board and tasks are deleted.`) && onRemove(m.id)}
            data-testid={`team-remove-${m.id}`} className="text-white/25 hover:text-red-400"><Trash2 className="h-4 w-4 inline" /></button>
        )}
      </td>
    </tr>
  );
}

export default function Team() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [creds, setCreds] = useState(null);
  const [err, setErr] = useState("");
  const load = () => api.get("/team").then((r) => { setData(r.data); setErr(""); })
    .catch((e) => setErr(e?.response?.data?.detail || "Could not load the team."));
  useEffect(() => { load(); }, []);

  if (user?.role !== "admin") {
    return (
      <div className="border border-gold-500/20 bg-navy-900/40 p-10 text-center text-white" data-testid="team-restricted">
        <Lock className="h-8 w-8 text-gold-500/50 mx-auto" />
        <p className="mt-4 font-light">Team administration is restricted to all-access administrators.</p>
      </div>
    );
  }

  const patch = async (id, body) => { await api.patch(`/team/${id}`, body); await load(); };
  const remove = async (id) => { await api.delete(`/team/${id}`); await load(); };

  return (
    <div className="space-y-8 text-white" data-testid="team-page">
      <div>
        <Mono className="text-gold-400 text-[0.65rem]">S-15 · Team administration</Mono>
        <h1 className="font-heading font-light text-4xl tracking-tight mt-3 flex items-center gap-3"><Users className="h-7 w-7 text-gold-400" /> Team</h1>
        <p className="text-white/50 font-light mt-2 max-w-2xl text-sm">
          Create sign-ins for new team members, set their access role and task board level, reset passwords and remove
          departures — all without a developer. New accounts land in the {data?.org?.name || "MDAu"} organisation automatically.
        </p>
      </div>
      {creds && <CredsBanner creds={creds} onClose={() => setCreds(null)} />}
      <InviteForm onDone={(m, password) => { setCreds(password ? { email: m.email, password } : null); load(); }} />
      {err && <p className="text-amber-300 text-sm" data-testid="team-error">{err}</p>}
      {!data && !err && <Loader2 className="h-5 w-5 animate-spin text-gold-400" />}
      {data && (
        <div className="border border-gold-500/20 bg-navy-900/40 p-5 overflow-x-auto">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Mono className="text-[0.6rem] text-gold-400">Members ({data.members.length})</Mono>
            {data.org?.join_code && <Mono className="text-[0.55rem] text-white/40">Field team join code: <span className="text-gold-300">{data.org.join_code}</span></Mono>}
          </div>
          <table className="w-full mt-3 min-w-[640px]">
            <thead><tr className="text-left border-b border-gold-500/20">
              {["Member", "Role", "Task board", ""].map((h) => <th key={h} className="pb-2 pr-4"><Mono className="text-[0.5rem] text-white/40">{h}</Mono></th>)}
            </tr></thead>
            <tbody>
              {data.members.map((m) => (
                <MemberRow key={m.id} m={m} self={m.id === user?.id} onPatch={patch} onRemove={remove} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
